import TransmissionInsightsMap from "../map/TransmissionInsightsMap";
import useMapboxAuth from "../hooks/useMapboxAuth";
import {
    UnauthenticatedTemplate,
    AuthenticatedTemplate,
    ReadAccessUnauthorizedTemplate,
    ReadAccessAuthorizedTemplate,
} from "../hooks/useAuth";
import { useTheme } from "../../contexts/ThemeContext";
import { DataTable } from "../map/DataTable";
import {
    useTransmissionLineData,
    useSubstationData,
    useGeneratorData,
} from "../../contexts/TransmissionInsightsContext";
import { substationColumns } from "../map/SubstationLayerColumns";
import { transmissionLineColumns } from "../map/TransmissionLineLayerColumns";
import { generatorColumns } from "../map/GeneratorLayerColumns";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/Tabs";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuItem,
    DropdownMenuContent,
} from "../ui/DropdownMenu";
import { Separator } from "../ui/Separator";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { OperatingGeneratorChart } from "../charts/OperatingGeneratorChart";
import { ThermalCapacityChart } from "../charts/ThermalCapacityChart";
import { ActiveQueuesChart } from "../charts/ActiveQueuesChart";
import {
    TRANSMISSION_INSIGHTS_CHARTS_PANEL,
    TRANSMISSION_INSIGHTS_DATA_TABLE,
    TRANSMISSION_INSIGHTS_MAP,
    NAV_PANEL,
} from "../constants/uiConstants";

type TableFilterOptions = "all" | "selected" | "visible";

const TransmissionInsightsPage = ({
    isPageExpanded,
}: {
    isPageExpanded: boolean;
}) => {
    const { mapboxAccessToken } = useMapboxAuth();
    const { currentTheme } = useTheme();
    const [tableFilterOption, setTableFilterOption] =
        useState<TableFilterOptions>("all");
    const substationData = useSubstationData(tableFilterOption);
    const transmissionLineData = useTransmissionLineData(tableFilterOption);
    const generatorData = useGeneratorData(tableFilterOption);

    const [viewCharts, setViewCharts] = useState<boolean>(false);
    const [viewTable, setViewTable] = useState<boolean>(false);

    return (
        <div className="flex bg-gray-200 dark:bg-gray-900 h-dvh z-0 max-w-screen">
            <UnauthenticatedTemplate>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100vh",
                        width: "100%",
                    }}
                >
                    <p>Please sign in to view this content!</p>
                    <button
                        onClick={() => {
                            window.location.href = "/.auth/login/aad";
                        }}
                        className="px-[6px] h-[30px] mt-1 rounded-[3px] bg-accent1 hover:bg-accent2 duration-300 text-white text-[13px] font-semibold"
                    >
                        Sign In
                    </button>
                </div>
            </UnauthenticatedTemplate>
            <AuthenticatedTemplate>
                <ReadAccessUnauthorizedTemplate>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100vh",
                            width: "100%",
                        }}
                    >
                        <p>
                            To view this content, please contact us at{" "}
                            <a href="mailto:services@grid-insights.net">
                                services@grid-insights.net
                            </a>
                            !
                        </p>
                    </div>
                </ReadAccessUnauthorizedTemplate>
                <ReadAccessAuthorizedTemplate>
                    <div className="flex w-full justify-between">
                        <div
                            className="flex flex-col p-2 w-full transition-[max-width] duration-300 ease-out"
                            style={{
                                maxWidth: viewCharts
                                    ? `calc(100vw - ${
                                          isPageExpanded
                                              ? NAV_PANEL.COLLAPSED.WIDTH +
                                                TRANSMISSION_INSIGHTS_CHARTS_PANEL.WIDTH
                                              : NAV_PANEL.EXPANDED.WIDTH +
                                                TRANSMISSION_INSIGHTS_CHARTS_PANEL.WIDTH
                                      }px)`
                                    : `calc(100vw - ${
                                          isPageExpanded
                                              ? NAV_PANEL.COLLAPSED.WIDTH
                                              : NAV_PANEL.EXPANDED.WIDTH
                                      }px)`,
                            }}
                        >
                            <div
                                className={`w-full ${
                                    !viewTable
                                        ? "h-[calc(100vh)]"
                                        : "h-[calc(100vh-340px)]"
                                }`}
                                style={{
                                    minWidth: `${TRANSMISSION_INSIGHTS_MAP.MIN_WIDTH}px`,
                                    minHeight: `${TRANSMISSION_INSIGHTS_MAP.MIN_HEIGHT}px`,
                                }}
                            >
                                {mapboxAccessToken === "" && "Loading…"}
                                {mapboxAccessToken !== "" && (
                                    <TransmissionInsightsMap
                                        mapboxAccessToken={mapboxAccessToken}
                                        theme={currentTheme}
                                        isPageExpanded={isPageExpanded}
                                        viewCharts={viewCharts}
                                        viewTable={viewTable}
                                        onViewChartsChange={setViewCharts}
                                        onViewTableChange={setViewTable}
                                    />
                                )}
                            </div>
                            {viewTable && (
                                <div
                                    className="w-full mt-2 bg-white dark:bg-[#2D3748] rounded-[4px] flex flex-col"
                                    style={{
                                        height: `${TRANSMISSION_INSIGHTS_DATA_TABLE.HEIGHT}px`,
                                        minWidth: `${TRANSMISSION_INSIGHTS_DATA_TABLE.MIN_WIDTH}px`,
                                    }}
                                >
                                    <Tabs
                                        defaultValue="substation"
                                        className="w-full h-[100%] flex flex-1 flex-col"
                                    >
                                        <div className="px-6 flex-none">
                                            <TabsList>
                                                <TabsTrigger value="substation">
                                                    Substation
                                                </TabsTrigger>
                                                <Separator
                                                    orientation="vertical"
                                                    className="h-[15px] w-[1.5px]"
                                                />
                                                <TabsTrigger value="transmission">
                                                    Transmission Line
                                                </TabsTrigger>
                                                <Separator
                                                    orientation="vertical"
                                                    className="h-[15px] w-[1.5px]"
                                                />
                                                <TabsTrigger value="generation">
                                                    Generation
                                                </TabsTrigger>
                                                <Separator
                                                    orientation="vertical"
                                                    className="h-[15px] w-[1.5px]"
                                                />
                                                <TabsTrigger
                                                    value="load"
                                                    disabled
                                                >
                                                    Load
                                                </TabsTrigger>
                                                <Separator
                                                    orientation="vertical"
                                                    className="h-[15px] w-[1.5px]"
                                                />
                                                <TabsTrigger
                                                    value="queue"
                                                    disabled
                                                >
                                                    Queue
                                                </TabsTrigger>
                                                <Separator
                                                    orientation="vertical"
                                                    className="h-[15px] w-[1.5px]"
                                                />
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger className="py-[4px] px-[8px] flex gap-4 border-[1px] border-[#C6C5C5] dark:border-[#7A8690] text-sm rounded-[4px] items-center focus-visible:outline-none hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] duration-200">
                                                        <div className="text-black dark:text-[#CEE0EF]">
                                                            {tableFilterOption ===
                                                            "all"
                                                                ? "Show All Features"
                                                                : tableFilterOption ===
                                                                    "selected"
                                                                  ? "Show Selected Features"
                                                                  : "Show Features Visible on Map"}
                                                        </div>
                                                        <ChevronDown className="h-4 w-4 stroke-[#D9D9D9] dark:stroke-[#7A8690]" />
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent className="bg-white dark:bg-[#2D3748]">
                                                        <DropdownMenuItem
                                                            className={`${
                                                                tableFilterOption ===
                                                                "all"
                                                                    ? "bg-[#C6D92D] dark:bg-[#C6D92D] hover:bg-[#C6D92D] hover:dark:bg-[#C6D92D] text-[#535353] dark:text-[#2D3748]"
                                                                    : "hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] text-[#535353] dark:text-[#CEE0EF]"
                                                            }`}
                                                            onClick={() =>
                                                                setTableFilterOption(
                                                                    "all"
                                                                )
                                                            }
                                                        >
                                                            Show All Features
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            className={`${
                                                                tableFilterOption ===
                                                                "selected"
                                                                    ? "bg-[#C6D92D] dark:bg-[#C6D92D] hover:bg-[#C6D92D] hover:dark:bg-[#C6D92D] text-[#535353] dark:text-[#2D3748]"
                                                                    : "hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] text-[#535353] dark:text-[#CEE0EF]"
                                                            }`}
                                                            onClick={() =>
                                                                setTableFilterOption(
                                                                    "selected"
                                                                )
                                                            }
                                                        >
                                                            Show Selected
                                                            Features
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            className={`${
                                                                tableFilterOption ===
                                                                "visible"
                                                                    ? "bg-[#C6D92D] dark:bg-[#C6D92D] hover:bg-[#C6D92D] hover:dark:bg-[#C6D92D] text-[#535353] dark:text-[#2D3748]"
                                                                    : "hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] text-[#535353] dark:text-[#CEE0EF]"
                                                            }`}
                                                            onClick={() =>
                                                                setTableFilterOption(
                                                                    "visible"
                                                                )
                                                            }
                                                        >
                                                            Show Features
                                                            Visible on Map
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TabsList>
                                        </div>
                                        <TabsContent
                                            value="substation"
                                            className="min-h-0 h-[100%]" // setting reasons in Document/tables.md
                                        >
                                            <DataTable
                                                columns={substationColumns}
                                                data={substationData}
                                            />
                                        </TabsContent>
                                        <TabsContent
                                            value="transmission"
                                            className="min-h-0 h-[100%]"
                                        >
                                            <DataTable
                                                columns={
                                                    transmissionLineColumns
                                                }
                                                data={transmissionLineData}
                                            />
                                        </TabsContent>
                                        <TabsContent
                                            value="generation"
                                            className="min-h-0 h-[100%]"
                                        >
                                            <DataTable
                                                columns={generatorColumns}
                                                data={generatorData}
                                            />
                                        </TabsContent>
                                        <TabsContent value="load">
                                            Load content
                                        </TabsContent>
                                        <TabsContent value="queue">
                                            Queue content
                                        </TabsContent>
                                    </Tabs>
                                </div>
                            )}
                        </div>
                        {viewCharts && (
                            <div
                                className="flex flex-col gap-2 w-full"
                                style={{
                                    maxWidth: `${TRANSMISSION_INSIGHTS_CHARTS_PANEL.WIDTH}px`,
                                }}
                            >
                                <div className="flex flex-col p-[15px] mt-2 mr-2 bg-white dark:bg-[#2D3748] rounded-[4px] max-h-[246px]">
                                    <span className="text-[#535353] dark:text-white font-semibold text-[15px]">
                                        Thermal Capacity Summary
                                    </span>
                                    <span className="text-[#535353] dark:text-[#CEE0EF] text-xs pb-4">
                                        August 2024
                                    </span>
                                    <ThermalCapacityChart />
                                </div>
                                <div className="flex flex-col p-[15px] mr-2 bg-white dark:bg-[#2D3748] rounded-[4px] max-h-[263px]">
                                    <span className="text-[#535353] dark:text-white font-semibold text-[15px]">
                                        Active Queues
                                    </span>
                                    <span className="text-[#535353] dark:text-[#CEE0EF] text-xs pb-4">
                                        August 2024
                                    </span>
                                    <ActiveQueuesChart />
                                </div>
                                <div className="flex flex-col p-[15px] mr-2 mb-2 pb-[22.5px] bg-white dark:bg-[#2D3748] rounded-[4px]">
                                    <span className="text-[#535353] dark:text-white font-semibold text-[15px]">
                                        Existing Operating Generators
                                    </span>
                                    <span className="text-[#535353] dark:text-[#CEE0EF] text-xs pb-4">
                                        August 2024
                                    </span>
                                    <OperatingGeneratorChart />
                                </div>
                            </div>
                        )}
                    </div>
                </ReadAccessAuthorizedTemplate>
            </AuthenticatedTemplate>
        </div>
    );
};

export default TransmissionInsightsPage;
